/**
 * Battle conclusion — strategic-map writeback + destroy + disband + retreat (v0M3.4a).
 *
 * Called by `battles.ts` on the tick a Battle transitions to `result !== null`.
 * Applies the battle's consequences back onto strategic-map state:
 *
 *  1. **Casualty writeback.** For every `CohortOnField` still on either side,
 *     copy the count of living figures (`killed === false`) back into the
 *     campaign `Cohort.aliveFigureCount`. Wounded figures count as alive
 *     per `BATTLES.md` §2.7.8.1.b sub-phase 4 — a wounded figure has been
 *     moved to the back row but is still on the roster; only `killed`
 *     figures are removed permanently.
 *
 *  2. **Destroy zero-figure cohorts.** A Cohort whose post-writeback
 *     `aliveFigureCount` is 0 is removed from `state.cohorts` and its id
 *     is spliced out of its owning Army's `cohortIds`. Broken-but-nonzero
 *     cohorts stay in the roster — a routed cohort has fled the field but
 *     its veterans persist. Recovery / replenishment mechanic is a v0M3.4b
 *     candidate.
 *
 *  3. **Disband empty-roster armies.** An Army whose `cohortIds` is empty
 *     after step 2 is removed from `state.armies` and its Commander is
 *     removed from `state.commanders`. Default commanders die with their
 *     army per §6.1 #14 v1 scope; Hero-commander survival is a post-MVP
 *     candidate.
 *
 *  4. **Retreat surviving loser(s).** Battles conclude only when a side
 *     is forced to withdraw (all cohorts broken or manual withdrawal —
 *     latter not yet supported). Surviving losers retreat along the
 *     vector *away* from the victor per Michael's v0M3.4a+ design lock
 *     (2026-07-12): position + `d × post_battle_retreat_distance`
 *     (default 9.0 cells). If the target cell is impassable per
 *     `terrainCost`, walk further along the same vector in 1-cell
 *     increments up to `post_battle_retreat_search_max` (default 27.0)
 *     total distance. If no passable cell found, the army is destroyed
 *     (Army + Commander removed). Search is strictly along the vector —
 *     no flank sampling — matching Michael's "no jump to flanks" rule.
 *     Mutual-withdrawal: both sides retreat by full retreat_distance in
 *     opposite directions along the axis between them.
 *
 * Order matters: writeback → destroy → disband → retreat. Retreat runs
 * after disband so armies wiped out by casualties are already gone and
 * don't get pointlessly repositioned.
 *
 * The function needs `SimContext` for terrain access (retreat depends on
 * passability). This is why it's called from `battles.ts` where the
 * context is already threaded.
 */

import type { Battle, CohortOnField } from './battle-state';
import type { SimContext } from './sim-context';
import { terrainCost } from './pathfind';
import type { Army, CohortId, GameState } from './state';
import { getDialNumber, loadDials } from '../data/dials';
import { captureCity } from './sieges';
// v0M10.6 (2026-07-24) — Commander level curve is now xlsx-authoritative
// via CommanderUnlocks. Replaces the hardcoded floor(xp/1000)+1 formula.
import { loadCommanderLevels, commanderLevelForXp } from '../data/commander-levels';
const BC_COMMANDER_LEVELS = loadCommanderLevels();

const DIALS = loadDials();
// MFix11.1 (2026-07-27) — retreat distance must clear the ZoC engagement
// threshold (2 × zoc_radius) or the loser lands back in ZoC range and
// re-engages next tick → infinite re-engagement loop (Michael playtest
// bug). Compute the floor at load time from `zoc_radius` and take the
// max against the dial-authored value.
const RAW_RETREAT_DISTANCE = getDialNumber(DIALS, 'post_battle_retreat_distance');
const RAW_RETREAT_SEARCH_MAX = getDialNumber(DIALS, 'post_battle_retreat_search_max');
const ZOC_RADIUS_FOR_RETREAT = getDialNumber(DIALS, 'zoc_radius');
// MFix15 (2026-07-29) — bumped ZoC margin 1.5 → 2 × zoc_radius so a
// retreating army lands a full extra ZoC diameter clear of the winner
// instead of just barely past touching distance. Michael's playtest
// report: the pre-MFix15 13.5-cell retreat (with R=6) let the loser
// walk back to re-engagement in under one game day; the winner would
// sit still and the loser would keep coming. The new floor pushes
// the loser to `4R + 0 = 24` cells (with R=6), giving ~2× as long
// for stamina/morale to recover before it can shove back into ZoC —
// combined with the isArmyWeak morale gate above, that breaks the
// grind loop.
const RETREAT_ZOC_MARGIN = 2 * ZOC_RADIUS_FOR_RETREAT;
const RETREAT_FLOOR = ZOC_RADIUS_FOR_RETREAT * 2 + RETREAT_ZOC_MARGIN;
const RETREAT_DISTANCE = Math.max(RAW_RETREAT_DISTANCE, RETREAT_FLOOR);
// Search window scales up if the floor bumped the distance; otherwise
// respect the authored search_max.
const RETREAT_SEARCH_MAX = Math.max(RAW_RETREAT_SEARCH_MAX, RETREAT_DISTANCE * 3);
const RETREAT_STEP = 1.0; // cells per search increment

/**
 * Resolve a concluded battle's strategic-map consequences. Requires
 * `battle.result !== null` — the caller (battles-system) enforces this.
 */
export function resolveBattleConclusion(
  state: GameState,
  battle: Battle,
  context: SimContext,
): void {
  if (battle.result === null) return;

  const armiesToInspect = new Set<Army>();
  // v0M4.0g: XP earned per Commander this battle, summed from every
  // on-field cohort's xpEarnedThisBattle counter (see battle-round.ts
  // strike bookkeeping).
  const xpPerCommander = new Map<import('./state').CommanderId, number>();

  for (const side of battle.sides) {
    for (const cof of side.cohorts.values()) {
      const cohort = state.cohorts.get(cof.id);
      if (!cohort) continue;
      const living = livingFigures(cof);
      cohort.aliveFigureCount = living;
      // v0M3.11: writeback final battle stamina + morale to the persistent
      // campaign Cohort. Clamped [0, 100] defensively — combat math can
      // drive stamina/morale below 0 during rout (§2.7.8.5), but the
      // persistent field only stores the survivor floor for map-side
      // recovery to work upward from.
      cohort.stamina = clamp01_100(cof.stamina);
      cohort.morale = clamp01_100(cof.morale);
      // v0M4.2.1: cof.armyId is nullable to allow garrison cohorts in
      // siege battles (v0M4.2.5). Field battles carry non-null armyIds
      // exclusively, but the null branch is safe: garrisons don't have
      // Commanders, so XP accrual is skipped; their casualty writeback
      // still runs above (aliveFigureCount / stamina / morale) and the
      // destroyCohort call below handles garrison-vs-army roster
      // detachment.
      if (cof.armyId !== null) {
        const army = state.armies.get(cof.armyId);
        if (army !== undefined) {
          armiesToInspect.add(army);
          // v0M4.0g: accumulate XP for this cohort's Commander.
          const prior = xpPerCommander.get(army.commanderId) ?? 0;
          xpPerCommander.set(army.commanderId, prior + cof.xpEarnedThisBattle);
        }
      }
      if (living === 0) {
        destroyCohort(state, cof.id);
      }
    }
  }

  // v0M4.0g: apply accumulated XP to each Commander + check rank
  // promotion at the 1,000-XP thresholds per UNITS.md §5.2. Rank
  // (level) capped at 20 per §5.4 — beyond that, XP keeps accruing
  // cosmetically but confers no additional cohort slot.
  for (const [commanderId, xpDelta] of xpPerCommander) {
    if (xpDelta <= 0) continue;
    const commander = state.commanders.get(commanderId);
    if (!commander) continue;
    commander.xp += xpDelta;
    // v0M10.6 (2026-07-24) — xlsx-authoritative. commanderLevelForXp
    // reads cumulative thresholds from CommanderUnlocks (100/250/500/
    // 800/1200/2000/3000/5000/10000 at v1) and caps at the table's
    // max level (10). Retires the hardcoded floor(xp/1000)+1 curve.
    const targetRank = commanderLevelForXp(commander.xp, BC_COMMANDER_LEVELS);
    const rankChanged = targetRank > commander.level;
    if (rankChanged) {
      commander.level = targetRank;
    }
    // v0M4.1.1 — log the commander-xp event (BATTLES.md §2.9.1). newLevel
    // non-null only when the commander was promoted this battle.
    battle.log.push({
      kind: 'commander-xp',
      tick: battle.round, // approximate — battle.round is the best time-stamp available at conclusion
      round: battle.round,
      commanderId,
      xpDelta,
      newLevel: rankChanged ? targetRank : null,
    });
  }

  for (const army of armiesToInspect) {
    if (army.cohortIds.length === 0) {
      disbandArmy(state, army);
    }
  }

  // v0M4.2.5 — siege-battle post-conclusion (BATTLES.md §3.5 / §3.8).
  // Branches on `Battle.siegeSettlement` non-null. For CAPITAL sieges,
  // skips applyRetreat — siege losers stay at their adjacency cell
  // (attacker: continues siege next tick / defender: garrison already
  // wiped by casualty writeback + city captured).
  //
  // MFix11.1 (2026-07-27) — MINOR-settlement siege battles (from
  // pillages.ts) DO run applyRetreat after resolveSiegeConclusion so
  // the attacker army gets pushed out of the minor's ZoC on defender
  // victory. Without this the attacker stays adjacent and pillages.ts
  // re-opens the siege next tick → infinite re-engagement loop.
  if (battle.siegeSettlement !== null) {
    resolveSiegeConclusion(state, battle, context);
    const siegedSettlement = state.settlements.get(battle.siegeSettlement);
    if (siegedSettlement?.isMinor) {
      applyRetreat(state, battle, context);
    }
    return;
  }

  // v0M7.4d+siege (2026-07-26) — Fort-assault post-conclusion. Mirrors
  // the settlement-siege branch above but flips WorldStructure
  // ownership + destroys the garrison cohorts on attacker victory.
  if (battle.siegeFortId != null) {
    resolveFortAssaultConclusion(state, battle);
    return;
  }

  applyRetreat(state, battle, context);
}

/**
 * v0M7.4d+siege (2026-07-26) — Fort-assault post-battle handler.
 *
 * Side 0 = defender (Fort garrison). Side 1 = attacker.
 * `battle.result` semantics:
 *   - `side1-victory` → attacker won → Fort ownership flips to the
 *     attacker's faction + all garrison cohorts destroyed. Attacker
 *     army stays adjacent (no explicit move — they were adjacent
 *     pre-battle by the assault_fort validation).
 *   - `side0-victory` → defender won → Fort keeps its owner + its
 *     surviving garrison. Casualty writeback on the sim-cohort list
 *     already happened before this handler runs, so the garrison's
 *     figure counts reflect the losses.
 *   - `mutual-withdrawal` → same as defender-victory. Fort unchanged.
 *
 * No siege state to clear (Fort assaults are direct — no timer). No
 * retreat path applied to the losing side per the settlement-siege
 * convention: siege-losers stay adjacent.
 */
function resolveFortAssaultConclusion(state: GameState, battle: Battle): void {
  const fortId = battle.siegeFortId;
  if (fortId == null) return;
  const fort = state.worldStructures?.get(fortId);
  if (!fort) return;

  if (battle.result === 'side1-victory') {
    // Attacker won. armyIds[0] is the attacker per
    // createFortAssaultBattleFromArmies convention.
    const attackerArmyId = battle.armyIds[0];
    const attacker = state.armies.get(attackerArmyId);
    if (attacker) {
      // Flip ownership.
      fort.ownerFactionId = attacker.ownerFactionId;
      // Destroy all garrison cohorts — Fort capture nukes the
      // garrison rather than transferring it. Player must recruit
      // fresh garrison via the Fort panel.
      if (fort.garrisonCohortIds) {
        for (const cid of fort.garrisonCohortIds) {
          const cohort = state.cohorts.get(cid);
          if (cohort) {
            cohort.aliveFigureCount = 0;
            state.cohorts.delete(cid);
          }
        }
        fort.garrisonCohortIds = [];
      }
    }
  }
  // Defender-victory + mutual-withdrawal: no explicit action. The
  // Fort ownership + garrison survive as-is (casualty writeback on
  // the persistent cohorts happened pre-handler).
}

/**
 * v0M4.2.5 — Siege-specific post-battle handler per BATTLES.md §3.5.
 *
 * By convention (createSiegeBattleFromArmies), side 0 = defender
 * (garrison + optional field army inside walls) and side 1 = attacker
 * (the sieger). Battle.result semantics:
 *   - `side1-victory` → attacker won → capture city per §3.8.
 *   - `side0-victory` → defender won → siege ends this cycle; sieges.ts
 *     will reopen a fresh siege next tick if the attacker army is still
 *     adjacent to the city (with a full timer reset per §3.4).
 *   - `mutual-withdrawal` → same as defender-victory — siege timer
 *     resets, attacker stays put.
 *
 * Attacker army position is not modified — they were already adjacent
 * pre-battle, and the design says "stay in place" post-siege regardless
 * of outcome (attacker keeps sieging on loss; on win they're already
 * next to the captured city).
 */
function resolveSiegeConclusion(state: GameState, battle: Battle, context: SimContext): void {
  const settlementId = battle.siegeSettlement;
  if (settlementId === null) return; // Defensive; caller already checked.
  const city = state.settlements.get(settlementId);
  if (!city) return;

  if (battle.result === 'side1-victory') {
    // Attacker won — capture the city per §3.8. armyIds[0] is the
    // attacker per createSiegeBattleFromArmies convention.
    const attackerArmyId = battle.armyIds[0];
    const attacker = state.armies.get(attackerArmyId);
    if (attacker) {
      captureCity(state, city, attacker, context);
    }
  }
  // Defender-victory + mutual-withdrawal: no explicit action. The
  // siege state was cleared when the attacker entered the battle
  // (sieges.ts break condition b); the attacker's position is
  // unchanged, so next-tick siegesSystem will detect the adjacency
  // and reopen the siege with a fresh `siege_surrender_days` timer.
}

/**
 * Count figures alive on the field — anything not `killed`. Wounded
 * figures count per §2.7.8.1.b sub-phase 4.
 */
export function livingFigures(cof: CohortOnField): number {
  let n = 0;
  for (const f of cof.figures) {
    if (!f.killed) n++;
  }
  return n;
}

/** Clamp a stamina/morale value into the persistent-storage range [0, 100]. */
function clamp01_100(v: number): number {
  if (v < 0) return 0;
  if (v > 100) return 100;
  return v;
}

// ---------------------------------------------------------------------------
// Retreat mechanic (v0M3.4a+)
// ---------------------------------------------------------------------------

/**
 * Push surviving loser(s) away from the victor per Michael's design lock.
 * See file-header §4 for the rule. No-op if the loser army was already
 * disbanded by casualties in step 3.
 */
function applyRetreat(state: GameState, battle: Battle, context: SimContext): void {
  const [id0, id1] = battle.armyIds;
  const army0 = state.armies.get(id0);
  const army1 = state.armies.get(id1);

  switch (battle.result) {
    case 'side0-victory':
      // Side 0 won; side 1 (army1) retreats away from army0.
      if (army1 !== undefined && army0 !== undefined) {
        retreatOrDestroy(state, army1, army0.position, context);
      }
      break;
    case 'side1-victory':
      if (army0 !== undefined && army1 !== undefined) {
        retreatOrDestroy(state, army0, army1.position, context);
      }
      break;
    case 'mutual-withdrawal':
      // Both broke; both retreat along the shared axis by full retreat_distance.
      if (army0 !== undefined && army1 !== undefined) {
        // Snapshot positions BEFORE any retreat so the axis is symmetric.
        const pos0 = { x: army0.position.x, y: army0.position.y };
        const pos1 = { x: army1.position.x, y: army1.position.y };
        retreatOrDestroy(state, army0, pos1, context);
        retreatOrDestroy(state, army1, pos0, context);
      }
      break;
    case null:
      // Guarded by resolveBattleConclusion; unreachable here.
      break;
  }
}

/**
 * Try to retreat `loser` along the vector `victorPos → loser.position`.
 * If a passable cell exists within `RETREAT_SEARCH_MAX` cells, place the
 * army there. Otherwise destroy the army (per Michael's spec).
 */
function retreatOrDestroy(
  state: GameState,
  loser: Army,
  victorPos: { x: number; y: number },
  context: SimContext,
): void {
  const dir = normalisedRetreatDirection(loser.position, victorPos);

  // Walk from RETREAT_DISTANCE outward in RETREAT_STEP increments.
  for (let d = RETREAT_DISTANCE; d <= RETREAT_SEARCH_MAX + 1e-9; d += RETREAT_STEP) {
    const tx = loser.position.x + dir.x * d;
    const ty = loser.position.y + dir.y * d;
    if (isPassableAt(context, tx, ty)) {
      loser.position.x = tx;
      loser.position.y = ty;
      // Any pending movement plan is stale after a forced retreat.
      loser.plannedPath = null;
      loser.movementGoal = null;
      return;
    }
  }

  // Landlocked — no valid retreat cell along the vector. Army destroyed.
  destroyAllCohortsOfArmy(state, loser);
  disbandArmy(state, loser);
}

/**
 * Unit vector from `victor` toward `loser` (i.e. the direction the loser
 * flees). Degenerate case (victor == loser position) falls back to +x
 * axis — this is a scenario-boot artefact that shouldn't happen in a
 * real battle (armies must be within ZoC overlap for battle to fire).
 */
function normalisedRetreatDirection(
  loserPos: { x: number; y: number },
  victorPos: { x: number; y: number },
): { x: number; y: number } {
  const dx = loserPos.x - victorPos.x;
  const dy = loserPos.y - victorPos.y;
  const mag = Math.hypot(dx, dy);
  if (mag < 1e-9) return { x: 1, y: 0 };
  return { x: dx / mag, y: dy / mag };
}

/**
 * True iff the cell at (floor(x), floor(y)) is inside the map and its
 * terrain has finite cost (i.e. is passable). Uses the same passability
 * rule as `pathfind.ts` so retreat matches move-order navigability.
 */
function isPassableAt(context: SimContext, x: number, y: number): boolean {
  const w = context.terrain.width;
  const h = context.terrain.height;
  const cx = Math.floor(x);
  const cy = Math.floor(y);
  if (cx < 0 || cx >= w || cy < 0 || cy >= h) return false;
  const id = context.terrain.terrain[cy * w + cx];
  if (id === undefined) return false;
  return Number.isFinite(terrainCost(id));
}

// ---------------------------------------------------------------------------
// Destroy / disband internals
// ---------------------------------------------------------------------------

function destroyCohort(state: GameState, cohortId: CohortId): void {
  const cohort = state.cohorts.get(cohortId);
  if (!cohort) return;
  // v0M4.2.1: cohorts have exactly one of armyId / settlementId non-null.
  // Detach from whichever roster owns the id, then delete the entity.
  if (cohort.armyId !== null) {
    const army = state.armies.get(cohort.armyId);
    if (army !== undefined) {
      const idx = army.cohortIds.indexOf(cohortId);
      if (idx >= 0) army.cohortIds.splice(idx, 1);
    }
  } else if (cohort.settlementId !== null) {
    const city = state.settlements.get(cohort.settlementId);
    if (city !== undefined) {
      const idx = city.garrisonCohortIds.indexOf(cohortId);
      if (idx >= 0) city.garrisonCohortIds.splice(idx, 1);
    }
  }
  state.cohorts.delete(cohortId);
}

/**
 * Wipe every Cohort in the army's roster (used when the army is
 * landlocked and gets destroyed at retreat time). Iterate a snapshot of
 * ids because destroyCohort mutates army.cohortIds.
 */
export function destroyAllCohortsOfArmy(state: GameState, army: Army): void {
  const ids = [...army.cohortIds];
  for (const cid of ids) destroyCohort(state, cid);
}

export function disbandArmy(state: GameState, army: Army): void {
  state.armies.delete(army.id);
  state.commanders.delete(army.commanderId);
}
